import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import BusinessPage from "@/pages/BusinessPage";
import GrowthPartnersPage from "@/pages/GrowthPartnersPage";
import LeadershipPage from "@/pages/LeadershipPage";
import CapabilitiesPage from "@/pages/CapabilitiesPage";
import IntelligencePage from "@/pages/IntelligencePage";
import BrandPage from "@/pages/BrandPage";
import DigitalPage from "@/pages/DigitalPage";
import StrategyPage from "@/pages/StrategyPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import EcosystemPage from "@/pages/EcosystemPage";
import InsightsPage from "@/pages/InsightsPage";
import IndustryDetailPage from "@/pages/IndustryDetailPage";
import ContactPage from "@/pages/ContactPage";
import CareersPage from "@/pages/CareersPage";
import InsightArticlePage from "@/pages/InsightArticlePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfUsePage from "@/pages/TermsOfUsePage";
import SitemapPage from "@/pages/SitemapPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ToolsHubPage from "@/pages/ToolsHubPage";
import ToolPage from "@/pages/ToolPage";
import HowWeEngagePage from "@/pages/HowWeEngagePage";
import SolutionsHubPage from "@/pages/SolutionsHubPage";
import SolutionPage from "@/pages/SolutionPage";
import { captureAttribution } from "@/lib/utm";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

/** Observes .reveal elements across route changes and fades them in. */
function RevealObserver() {
  const { pathname } = useLocation();
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    const bind = () =>
      document
        .querySelectorAll(".reveal:not(.is-visible)")
        .forEach((el) => io.observe(el));
    bind();
    const mo = new MutationObserver(bind);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    captureAttribution();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <RevealObserver />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/businesses/growth-partners" element={<GrowthPartnersPage />} />
          <Route path="/businesses/:slug" element={<BusinessPage />} />
          <Route path="/leadership" element={<LeadershipPage />} />
          <Route
            path="/capabilities/digital-commerce"
            element={<DigitalPage />}
          />
          <Route
            path="/capabilities/brand-marketing-communications"
            element={<BrandPage />}
          />
          <Route
            path="/capabilities/market-customer-intelligence"
            element={<IntelligencePage />}
          />
          <Route
            path="/capabilities/strategy-commercial-transformation"
            element={<StrategyPage />}
          />
          <Route path="/capabilities/:slug" element={<CapabilitiesPage />} />
          <Route
            path="/capabilities/:slug/:subSlug"
            element={<ServiceDetailPage />}
          />
          <Route path="/ecosystem" element={<EcosystemPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/insights/:slug" element={<InsightArticlePage />} />
          <Route path="/industries/:slug" element={<IndustryDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/how-we-engage" element={<HowWeEngagePage />} />
          <Route path="/solutions" element={<SolutionsHubPage />} />
          <Route path="/solutions/growth360" element={<SolutionPage solutionId="growth360" />} />
          <Route path="/solutions/profitshield" element={<SolutionPage solutionId="profitshield" />} />
          <Route path="/solutions/salesmax" element={<SolutionPage solutionId="salesmax" />} />
          <Route path="/solutions/channelmax" element={<SolutionPage solutionId="channelmax" />} />
          <Route path="/solutions/commercemax" element={<SolutionPage solutionId="commercemax" />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/tools" element={<ToolsHubPage />} />
          <Route path="/tools/:slug" element={<ToolPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors closeButton theme="light" />
    </div>
  );
}

export default App;
